/**
 @Developper: BenAli Faker
 @Company: SMART-INFO
 @Post: CTO
 @Date:07/11/2020
 @Time:08:52
 **/
import Swal from 'sweetalert2';
import TranslationService from "../../i18n/service/TranslationService";

/**
 * les sélecteurs a utiliser avec bootstrap modal
 * @type {{dialogContainerID: string, dialogBodySelector: string}}
 */
const selectors = {
    dialogContainerID: 'begro-ajax-form',
    dialogFormContainer: '.modal-body',
    submitButton: '.btn-primary'
}

/**
 * Classe qui permet d'afficher des boite de dialogues (fenêtres)
 */
class Dialog {

    /**
     * @description module qui sera utilisé pour afficher la boite de dialogue
     * @type {Readonly<{sweet_alert: string, modal_bootstrap: string}>}
     */
    static module = Object.freeze({sweet_alert: 'sweet_alert', modal_bootstrap: 'modal_bootstrap'})

    /**
     * template underscore pour modal bootstrap
     */
    static #_bootstrapModalTemplate;

    /**
     * @description Les positions disponibles pour une fenêtre modal bootstrap
     * @type {Readonly<{left: string, center: string, right: string, fill: string}>}
     */
    static bootstrapModalPosition = Object.freeze({left: 'left', right: 'right', center: 'center', fill: 'fill'})

    /**
     * @description énumération pour les types de fenêtre ou notice existant
     * @type {Readonly<{aucun:boolean,success: string, warning: string, error: string, info: string}>}
     */
    static TypeEnum = Object.freeze({
        "aucun": "default",
        "error": "error",
        "warning": "warning",
        "success": "success",
        "question": "question",
        "info": "info"
    });

    /**
     * @description les méthodes HTTP
     * @type {Readonly<{post: string, get: string, update: string, delete: string}>}
     */
    static HttpMethod = Object.freeze({post: "POST", get: "GET", delete: "DELETE", put: "PUT"});


    static set bootstrapModalTemplate(value) {
        Begro.logInfo("Affectation de la template bootstrap modal pour utilisation avec le module notice et dialog");
        this.#_bootstrapModalTemplate = value;
    }

    /**
     *
     * @param confirmButtonTitle le nom du bouton confirmation
     * @param {String=} title le titre de la fenêtre
     * @param {Node|String} content le continu de la fenêtre
     * @param {TypeEnum}type le type de la fenêtre
     * @param {boolean} isAjaxRequest  permet d'ajouter ou non configuration liés au fenêtre avec traitement ajax
     * @return {{icon: string, showCancelButton: boolean, confirmButtonText: (*|string), cancelButtonText: string}}
     */
    static #generateSwalConfig(confirmButtonTitle, title, content, type, isAjaxRequest) {
        let swalConfig = {
            showCancelButton: true,
            confirmButtonText: confirmButtonTitle || TranslationService.getInstance().translate("commun:action:confirmer"),
            cancelButtonText: TranslationService.getInstance().translate("commun:action.annuler"),
            //closeOnConfirm: false,
            //closeOnCancel: true,
            //dangerMode: type === this.TypeEnum.warning,
        }
        if (type === this.TypeEnum.warning) {
            swalConfig['confirmButtonColor'] = "#DD6B55";
            swalConfig['icon'] = this.TypeEnum.warning;
        }
        if (title != null) {
            Begro.logInfo("Le titre de la fenêtre est :" + title);
            swalConfig['title'] = title;
        }
        if (content !== null) {
            Begro.logInfo("Le continu de la fenêtre est :" + content);
            swalConfig['html'] = content;
        }
        if (isAjaxRequest) {
            swalConfig['allowOutsideClick'] = () => !Swal.isLoading();
            swalConfig['showLoaderOnConfirm'] = true;
            swalConfig['allowEscapeKey'] = false;
            swalConfig['backdrop'] = true;
        }
        return swalConfig;
    }


    /**
     * Code partagé pour attachement des traitement en cas de succès ou annulation
     * @param {Promise}promise swal promise
     * @param {function=} successCallback le traitement a faire en cas d'acceptation
     * @param {function=} cancelCallback le traitement a faire en cas d'annulation
     */
    static #attachSwalPromesseTraitement(promise, successCallback, cancelCallback) {
        Begro.logInfo("Attachement promise sweet callbacks")
        promise.then(result => {
            if (typeof successCallback == "function" && result.isConfirmed) {
                let value = result.value;
                Begro.logInfo("Lancement de la fonction de rappel - succès");
                if (value.hasOwnProperty("statut") && value.statut === "success" && value.hasOwnProperty("feedbackMessage")) {
                    Notice.success(value.feedbackMessage);
                } else {//réponse succées le continu de la notice sera le continu de la réponse
                    Notice.success(value);
                }
                successCallback(value.hasOwnProperty("data") ? value.data : value);//si un json et contient propriété data on passe directement au fonction callback
            } else { //annulation
                Begro.logInfo("Raison d'annulation:" + result.dismiss);
                if (typeof cancelCallback == "function") {
                    Begro.logInfo("Lancement de la fonction de rappel - annulation")
                    cancelCallback();
                }
            }
        }, reason => {
            Notice.error(reason);
            Begro.logInfo("sweet alert rejeté a cause d'une erreur : " + reason);
        });
    }

    /**
     *
     * @Description Boite de dialogue confirmation avec appel de url ajax pour traitement
     * @param {String=} confirmButtonTitle le titre de bouton confirmation si null on affiche confirm par défaut
     * @param {String} title le titre de la fenêtre de confirmation
     * @param {Node|String} content le continu de la fenêtre de confirmation
     * @param {function=}onCancelCallback
     * @param {function=} onSuccessCallback
     * @param {{}} data les données a envoyer
     * @param {HttpMethod} method méthode HTTP
     * @param {TypeEnum} type le type de la fênetre de confirmation
     * @param url lien qui sera invoqué si l''utilisateur confirm l'action
     */
    static #confirmationWithUrlInvoke(confirmButtonTitle, title, content, type, onCancelCallback, onSuccessCallback, url, data, method) {
        let swal;
        let swalConfig = this.#generateSwalConfig(confirmButtonTitle, title, content, type, true);
        swalConfig['preConfirm'] = () => $.when($.ajax({
            'method': method,
            'url': url,
            'data': data
        })).then(
            (data, textStatus, jqXHR) => {//promesse succès
                Begro.logInfo("Dialog avec appel jax URL : Lancement de la fonction de rappel en cas de succès");
                return data;
            }
        ).catch(error => {
            swal.close();// pour lancer l'exécution on la partie then de promesse de sweetalert
            Notice.error(error.responseText);
        });
        swal = Swal.fire(swalConfig);
        this.#attachSwalPromesseTraitement(swal, onSuccessCallback, onCancelCallback);
    }


    /**
     * @desMéthode qui  permet d'ouvrir uen boite de dialogue - confirmation en définissant les action a faire en cas d'accept ou annulation
     * @param {String=} confirmButtonTitle le titre de bouton confirmation si null on affiche confirm par défaut
     * @param {String} title le titre de la fenêtre de confirmation
     * @param {Node|String} content le continu de la fenêtre de confirmation
     * @param {TypeEnum} type
     * @param {function=}onCancelCallback
     * @param {function=}onSuccessCallback
     */
    static #launchConfirmation(confirmButtonTitle, title, content, type, onCancelCallback, onSuccessCallback) {
        let swalConfig = this.#generateSwalConfig(confirmButtonTitle, title, content, type, false);
        this.#attachSwalPromesseTraitement(Swal.fire(swalConfig), onSuccessCallback, onCancelCallback);

    }

    /**
     *
     * @Description Boite de dialogue confirmation format alerte avec appel de url ajax pour traitement
     * @param {String=} confirmButtonTitle le titre de bouton confirmation si null on affiche confirm par défaut
     * @param {String} title le titre de la fenêtre de confirmation
     * @param {Node|String} content le continu de la fenêtre de confirmation
     * @param {function=}onCancelCallback
     * @param {function=}onSuccessCallback
     * @param {{}=} data les données a envoyer
     * @param {HttpMethod=} method méthode HTTP par défaut get
     * @param url lien qui sera invoqué si l''utilisateur confirm l'action
     *
     */
    static confirmationWarningWithUrlInvoke(confirmButtonTitle, title, content, onCancelCallback, onSuccessCallback, url, data, method = this.HttpMethod.get) {
        this.#confirmationWithUrlInvoke(confirmButtonTitle, title, content, this.TypeEnum.warning, onCancelCallback, onSuccessCallback, url, data, method)
    }

    static standardConfirmationWithUrlInvoke(confirmButtonTitle, title, content, onCancelCallback, onSuccessCallback, url, data, method = this.HttpMethod.get) {
        this.#confirmationWithUrlInvoke(confirmButtonTitle, title, content, this.TypeEnum.info, onCancelCallback, onSuccessCallback, url, data, method)
    }


    /**
     * @desMéthode qui  permet d'ouvrir uen boite de dialogue - confirmation en mode alerte
     * @param {String=} confirmButtonTitle le titre de bouton confirmation si null on affiche confirm par défaut
     * @param {String} title le titre de la fenêtre de confirmation
     * @param {Node|String} content le continu de la fenêtre de confirmation
     * @param {function=}onCancelCallback
     * @param {function=}onSuccessCallback
     */
    static confirmationWarning(confirmButtonTitle, title, content, onCancelCallback, onSuccessCallback) {
        this.#launchConfirmation(confirmButtonTitle, title, content, this.TypeEnum.warning, onCancelCallback, onSuccessCallback);
    }

    /**
     * Méthode qui  permet d'ouvrir uen boite de dialogue - confirmation normale
     * @param {String=} confirmButtonTitle le titre de bouton confirmation si null on affiche confirm par défaut
     * @param {String} title le titre de la fenêtre de confirmation
     * @param {Node|String} content le continu de la fenêtre de confirmation
     * @param {function=}onCancelCallback
     * @param {function=}onSuccessCallback
     */
    static confirmation(confirmButtonTitle, title, content, onCancelCallback, onSuccessCallback) {
        this.#launchConfirmation(confirmButtonTitle, title, content, this.TypeEnum.warning, onCancelCallback, onSuccessCallback);
    }

    /**
     * @description Méthode qui  permet d'ouvrir uen boite de dialogue pour édition d'une formulaire qui sera récupéré et traité via ajax
     * en cas de succès le serveur doit retourne un objet json de la forme {{'data':{},'feedbackMessage':string}}
     * en cas d'erreur le serveur doit retourner la formulaire qui sera re-injecté dans la boite de dialog
     * @param {String=} confirmButtonTitle le titre de bouton confirmation si null on affiche confirm par défaut
     * @param {String} title le titre de la fenêtre de confirmation
     * @param {String} submitURL URL de submit de la formulaire
     * @param {String} getFormURL URL de récupération de la formulaire
     * @param {function=}onCancelCallback
     * @param {function=}onSuccessCallback
     * @param {{Dialog.module}}module le module qui sera utilisé pour afficher la boite de dialogue part défaut bootstrap
     *
     */
    static ajaxForm(confirmButtonTitle, title, onCancelCallback, onSuccessCallback, getFormURL, submitURL, module = Dialog.module.modal_bootstrap) {
        module === Dialog.module.modal_bootstrap ?
            this.#ajaxFormBootstrapModal(confirmButtonTitle, title, onCancelCallback, onSuccessCallback, getFormURL, submitURL)
            :
            this.#ajaxFormSweetAlert(confirmButtonTitle, title, onCancelCallback, onSuccessCallback, getFormURL, submitURL);
    }


    /**
     *
     * @param {function(string)=} onFailureFormSubmit le traitement a faire si les données ne sont pas valide
     * @param {function(Object)=} onSuccessFormSubmit le traitement a faire une fois les la formulaire et validé
     * @param {function:Object=} getDataToSubmit retourne les données qui seront envoyés au serveur
     * @param {string}submitURL
     * @return Promise
     */
    static #submitFormAjax(onFailureFormSubmit, onSuccessFormSubmit, getDataToSubmit, submitURL) {
        let operationEncoursDialog;
        return $.when(
            $.ajax({
                    method: this.HttpMethod.post,
                    url: Begro.getURL(submitURL),
                    contentType: false,
                    cache: false,
                    processData: false,
                    beforeSend:function(){
                       operationEncoursDialog=Dialog.displayOperationEnCours();
                    },
                    complete: function(){
                        operationEncoursDialog.close();
                    },
                    data: getDataToSubmit()//on délègue la récupération des données à la méthode appelante
                }
            )).then(
            /**
             *
             * @param responseData {{'data':{},'feedbackMessage':string}|String} //data un objet json dépend de la réponse et la page demandé
             * @param {String} textStatus data
             * @param {Object} jqXHR
             */
            (responseData, textStatus, jqXHR) => {//promesse succès
                Begro.logInfo("Submit form ajax response type json,", responseData);
                if (jqXHR.responseJSON) {//si la reponse et json c'est ok
                    Begro.logInfo("Succès -submit form et réponse type json");
                    //affichage de la notice
                    return onSuccessFormSubmit(responseData);
                } else { //sinon erreur validation form et on affiche le nouveau form
                    Begro.logInfo("La valeur de retour de l'envoie de la formulaire  est un autre  type différent de json  => formulaire est non valide ");
                    return onFailureFormSubmit(responseData);
                }
            }
        ).catch(error => {
            Notice.error(error);
        });
    }

    /**
     * @desc code commun pour les boite de dialogue de type ajax qui s'occupe de la récupération de la formulaire et de déléguer l'affichage au module concerné sweet alert ou bootstrap modal
     * @param {string} title le titre de la formulaire
     * @param {function(form:string)} onFormReady le traitement a faire une fois on le continu de la formulaire
     * @param {string} formURL l'url de récupération de la formulaire a afficher
     */
    static #displayFormAjax(title, onFormReady, formURL) {
        Begro.logInfo("Récupération de la formulaire" + title + " pour son traitement");
        $.when($.ajax({//récupération de la formulaire
            url: Begro.getURL(formURL),
            method: "GET"
        })).then((content, textStatus, jqXHR) => {
            onFormReady(content);
        }).catch(error => {
            Notice.error(error.responseText);
        })
    }


    /**
     * @description Méthode qui  permet d'ouvrir uen boite de dialogue pour édition d'une formulaire qui sera récupéré et traité via ajax
     * en cas de succès le serveur doit retourne un objet json de la forme {{'data':{},'feedbackMessage':string}}
     * en cas d'erreur le serveur doit retourner la formulaire qui sera re-injecté dans la boite de dialog
     * @param {String=} confirmButtonTitle le titre de bouton confirmation si null on affiche confirm par défaut
     * @param {String} title le titre de la fenêtre de confirmation
     * @param {String} submitURL URL de submit de la formulaire
     * @param {String} getFormURL URL de récupération de la formulaire
     * @param {function=}onCancelCallback
     * @param {function=}onSuccessCallback
     * Note
     */
    static #ajaxFormSweetAlert(confirmButtonTitle, title, onCancelCallback, onSuccessCallback, getFormURL, submitURL) {
        let swalInstance;//instance sweet alert
        this.#displayFormAjax(title, function (form) {
            let swalConfig = Dialog.#generateSwalConfig(confirmButtonTitle, title, form, Dialog.TypeEnum.aucun, true);
            //pre-confirm sweet alert traitement
            swalConfig['preConfirm'] = () => Dialog.#submitFormAjax(
                //lorsque la formulaire n'est pas valide
                responseData => {
                    swalInstance.update({'html': responseData});
                    return false;
                }, //lorsque la formulaire est valide
                responseData => {
                    return responseData;
                },
                //récupérer les données remplis par l'utilisateur pour l'envoyer au serveur
                () => {
                    let dataToSubmit = $(Swal.getHtmlContainer()).find('form').serialize();
                    Begro.logInfo(`Donnée qui seront envoyé ajax form : ${dataToSubmit}`);
                    return new FormData($(Swal.getHtmlContainer()).find('form').get(0));
                },//url d'envoie des données
                submitURL);
            /**fin sweet alert pre-confirm traitement **/
            swalInstance = Swal.fire(swalConfig);
            Dialog.#attachSwalPromesseTraitement(swalInstance, onSuccessCallback, onCancelCallback);
        }, getFormURL);
    }


    /**
     * @description Méthode qui  permet d'ouvrir uen boite de dialogue pour édition d'une formulaire qui sera récupéré et traité via ajax
     * en cas de succès le serveur doit retourne un objet json de la forme {{'data':{},'feedbackMessage':string}}
     * en cas d'erreur le serveur doit retourner la formulaire qui sera re-injecté dans la boite de dialog
     * @param {String=} confirmButtonTitle le titre de bouton confirmation si null on affiche confirm par défaut
     * @param {String} title le titre de la fenêtre de confirmation
     * @param {String} submitURL URL de submit de la formulaire
     * @param {String} getFormURL URL de récupération de la formulaire
     * @param {function=}onCancelCallback
     * @param {function=}onSuccessCallback
     * Note
     */
    static #ajaxFormBootstrapModal(confirmButtonTitle, title, onCancelCallback, onSuccessCallback, getFormURL, submitURL) {
        let bootstrapModal = null;
        this.#displayFormAjax(title, function (form) {
            //créer une boite de dialog a partir d'une template bootstrap
            let dialog = $(Dialog.#_bootstrapModalTemplate({
                'id': selectors.dialogContainerID,
                'type': Dialog.TypeEnum.aucun,
                'position': Dialog.bootstrapModalPosition.right,
                'title': title,
                'content': form,
                'buttonSubmitTitle': confirmButtonTitle
            }));
            $('body').append(dialog);
            bootstrapModal = new bootstrap.Modal(dialog.get(0), {
                keyboard: false,
            });
            bootstrapModal.show();
            dialog.get(0).addEventListener('hidden.bs.modal', function (event) {
                dialog.remove();
            })
            //attacher un écouteur click au bouton confirmation
            $(`#${selectors.dialogContainerID} ${selectors.submitButton}`).on('click', function () {
                //submit form et les traitement associé
                Dialog.#submitFormAjax(
                    //lorsque la formulaire n'est pas valide
                    responseData => {
                        $(`#${selectors.dialogContainerID} ${selectors.dialogFormContainer}`).html(responseData);
                        bootstrapModal.handleUpdate();// mettre a jour
                        return false;
                    }, //lorsque la formulaire est valide
                    responseData => {
                        bootstrapModal.hide();
                        //bootstrapModal.dispose();// on détruit les données de la fenêtre modal sauvegardé dans le DOM
                        //dialog.remove();
                        //exécuter la méthode de rappel succées
                        if (responseData.hasOwnProperty("data")) {
                            onSuccessCallback(responseData.data);
                        } else {
                            onSuccessCallback();
                        }
                        //affichage de la notice succées
                        if (responseData.hasOwnProperty("statut") && responseData.statut === "success" && responseData.hasOwnProperty("feedbackMessage")) {
                            Notice.success(responseData.feedbackMessage);
                        } else {//réponse succées le continu de la notice sera le continu de la réponse
                            Notice.success(responseData);
                        }
                        return responseData;
                    },
                    //récupérer les données remplis par l'utilisateur pour l'envoyer au serveur
                    () => {
                        return new FormData($(`#${selectors.dialogContainerID} ${selectors.dialogFormContainer}`).find('form').get(0));
                    },//url d'envoie des données
                    submitURL);
            });
        }, getFormURL);
    }


    /**
     * Lancer une fenêtre d'alerte erreur,succès..
     * @param title
     * @param content
     * @param type
     * @return {Swal}
     */
    static #launchAlertInfo(title, content, type) {
        let swalConfig = {}
        if (title != null) {
            swalConfig['title'] = title;
        }
        if (content !== null) {
            swalConfig['content'] = content
        }
        swalConfig['button'] = {
            text: TranslationService.getInstance().translate("commun:ok").toUpperCase()
        };
        swalConfig['icon'] = type;
        return Swal.fire(swalConfig)
    }

    /**
     * afficher une boite alerte info
     * @param {String}title le titre de la boite d'alerte
     * @param {String}content le continu de la boite d'alerte
     */
    static alertInfo(title, content) {
        this.#launchAlertInfo(title, content, this.TypeEnum.info);
    }

    /**
     * afficher une boite alerte info succés avec bouton ok
     * @param {String}title le titre de la boite d'alerte
     * @param {String}content le continu de la boite d'alerte
     */
    static alertSuccess(title, content) {
        this.#launchAlertInfo(title, content, this.TypeEnum.success);
    }

    /**
     * afficher une boite alerte warning
     * @param {String}title le titre de la boite d'alerte
     * @param {String}content le continu de la boite d'alerte
     */
    static alertWarning(title, content) {
        this.#launchAlertInfo(title, content, this.TypeEnum.warning);
    }

    /**
     * afficher une boite alerte error avec bouton ok
     * @param {String}title le titre de la boite d'alerte
     * @param {String}content le continu de la boite d'alerte
     */
    static alertError(title, content) {
        this.#launchAlertInfo(title, content, this.TypeEnum.error);
    }


    /**
     * Affiche Opération En cours sur toute la fenêtre et retourne l'objet correspondant
     * @param title {String?} le text qui sera affiché pour l'opération en cours si non définit on affiche le texte par défaut
     * @returns {Promise<SweetAlertResult<Awaited<boolean>>>}
     */
    static displayOperationEnCours(title)
    {
        return Swal.fire({
            showCancelButton: false,
            showConfirmButton: false,
            allowOutsideClick: false,
            showLoaderOnConfirm: true,
            allowEscapeKey: false,
            preConfirm: () => {
                return true
            },
            backdrop: true,
            title: title || TranslationService.getInstance().translate("commun:patientez_operation_en_cours"),
            html: "<div class='loading'></div>",
        });
    }

    /**
     * @description lance une fenêtre en cours de Téléchargement en invoquant url passé en paramètre
     * @param {String} url url du fichier a télécharger
     * @param {String=} title de la fenêtre de téléchargement
     */
    static download(url, title = null) {
        let downloadDialog;
        $.ajax({
            url: url,
            cache: false,
            xhrFields: {
                responseType: 'blob'
            },
            beforeSend: function () {
                Begro.logInfo("Dialog et Notice : Le téléchargement ajax de fichier va commencer");
                downloadDialog = Swal.fire({
                    showCancelButton: false,
                    showConfirmButton: false,
                    allowOutsideClick: false,
                    showLoaderOnConfirm: true,
                    allowEscapeKey: false,
                    preConfirm: () => {
                        return true
                    },
                    backdrop: true,
                    title: title || TranslationService.getInstance().translate("commun:patientez_operation_en_cours"),
                    html: "<div class='loading'></div>",
                });
            }
        }).done((data, textStatus, xhr) => {
            const contentType = xhr.getResponseHeader('Content-type').replace(/(.*);.*/, '$1');
            const fileName = xhr.getResponseHeader('Content-Disposition').replace(/.*filename=(.*)/, '$1');
            const urlObject = window.URL.createObjectURL(data);
            Begro.logInfo(`Dialog et Notice : le continu du fichier est récupéré sous le nom ${fileName} et de type ${contentType}`);
            let temporaryJqueryLink = $("<a/>").css("display", "none").attr({
                href: urlObject,//url object contenant le fichier téléchargé
                download: fileName//nom de fichier
            });
            $("body").append(temporaryJqueryLink);
            temporaryJqueryLink.get(0).click();
            window.URL.revokeObjectURL(url);
            Begro.logInfo(`Dialog et Notice : Fin de Téléchargement de fichier ${fileName}`);
            //fermer sweet alert dialog
            downloadDialog.close();

        })
            .fail(xhr => {
                Notice.error(TranslationService.getInstance().translate("commun:opeartion_echouee_message", {error: xhr.responseText}));
            });
    }
}

/**
 * classe qui permet d'afficher des messages notices
 */
class Notice {


    /**
     * template HTML notice avec image  qui sera traité avec underscore mdoule
     */
    static #_templateNoticeWithIcon;

    /**
     * template HTML notice avec icon  qui sera traité avec underscore module
     */
    static #_templateNoticeWithImage;


    /**
     * énumération pour les types de fenêtre ou notice existant
     * @type {Readonly<{success: string, warning: string, error: string, info: string}>}
     */
    static  PositionEnum = Object.freeze({
        bottomLeft: "bottom-start",
        bottomRight: "bottom-end",
        bottomCenter: "bottom-center",
        topRight: "top-end",
        topLeft: "top-start",
        topCenter: "top-center",
        mdiCenter: "mdi-center"
    });

    /**
     * les type de notice qu'on va utiliser
     * @type {Readonly<{simpleAlert: string, alertWithImage: string, fullwidth: string}>}
     */
    static viewMode = Object.freeze({
        pageFullWidth: "full-width-page",//largeur de la page
        autoWidthContent: "fill-content"// cumulation de son continu

    });


    static set templateNoticeWithIcon(value) {
        Begro.logInfo("Affectation de la template notice avec icône pour utilisation avec le module notice et dialog");
        this.#_templateNoticeWithIcon = value;
    }

    static set templateNoticeWithImage(value) {
        Begro.logInfo("Affectation de la template notice avec image pour utilisation avec le module notice et dialog");
        this.#_templateNoticeWithImage = value;
    }

    /**
     * Affiche une notice d'info avec image - titre et continu
     * @param {String|Node}content le continu de la notice
     * @param {String=}title le titre de la notice
     * @param {String=}imageURL url de image si viewMode est notice-with-image
     * @param {String} position la position left,right ....
     * @param {viewMode=} viewMode le mode d'affichage de la notice |si null on utilise le mode fill content
     * Note: si null par défaut notice simple
     */
    static infoWithImage(content, title, imageURL, position = Notice.PositionEnum.topRight, viewMode = Notice.viewMode) {
        Notice.#withImage(content, title, imageURL, Dialog.TypeEnum.info, position, viewMode);
    }

    /**
     * Affiche une notice d'erreur avec image - titre et continu
     * @param {String|Node}content le continu de la notice
     * @param {String=}title le titre de la notice
     * @param {String=}imageURL url de image si viewMode est notice-with-image
     * @param {String} position la position left,right ....
     * @param {viewMode=} viewMode le mode d'affichage de la notice  |si null on utilise le mode fill content
     * Note: si null par défaut notice simple
     */
    static errorWithImage(content, title, imageURL, position = Notice.PositionEnum.topRight, viewMode = Notice.viewMode) {
        Notice.#withImage(content, title, imageURL, Dialog.TypeEnum.error, position, viewMode);
    }

    /**
     * Affiche une notice d'alerte avec image - titre et continu
     * @param {String|Node}content le continu de la notice
     * @param {String=}title le titre de la notice
     * @param {String=}imageURL url de image si viewMode est notice-with-image
     * @param {String} position la position left,right ....
     * @param {viewMode=} viewMode le mode d'affichage de la notice  |si null on utilise le mode fill content
     * Note: si null par défaut notice simple
     */
    static warningWithImage(content, title, imageURL, position = Notice.PositionEnum.topRight, viewMode = Notice.viewMode) {
        Notice.#withImage(content, title, imageURL, Dialog.TypeEnum.warning, position, viewMode);
    }

    /**
     * Affiche une notice de succès avec image - titre et continu
     * @param {String|Node}content le continu de la notice
     * @param {String}title le titre de la notice
     * @param {String} position la position left,right ....
     * @param {String}imageURL url de image si viewMode est notice-with-image
     * @param {viewMode=} viewMode le mode d'affichage de la notice  |si null on utilise le mode fill content
     */
    static successWithImage(content, title, imageURL, position = Notice.PositionEnum.topRight, viewMode = Notice.viewMode) {
        Notice.#withImage(content, title, imageURL, Dialog.TypeEnum.success, position, viewMode);
    }


    /**
     *  Affiche une notice d'info avec icône - titre et continu
     * @param {String|Node}content le continu de la notice
     * @param {String=}title le titre de la notice
     * @param {String} position la position left,right ....
     * @param {String=}iconClasses url de image si viewMode est notice-with-image
     * @param {viewMode=} viewMode le mode d'affichage de la notice  |si null on utilise le mode fill content
     * Note: si null par défaut notice simple
     */
    static infoWithIcon(content, title, iconClasses, position, viewMode) {
        Notice.#withIcon(content, title, iconClasses, Dialog.TypeEnum.info, position = Notice.PositionEnum.topRight, viewMode = Notice.viewMode);
    }

    /**
     * Affiche une notice d'erreur avec icône - titre et continu
     * @param {String|Node}content le continu de la notice
     * @param {String=}title le titre de la notice
     * @param {String} position la position left,right ....
     * @param {String=}iconClasses de image si viewMode est notice-with-image
     * @param {viewMode=} viewMode le mode d'affichage de la notice  |si null on utilise le mode fill content
     * Note: si null par défaut notice simple
     */
    static errorWithIcon(content, title, iconClasses, position = Notice.PositionEnum.topRight, viewMode = Notice.viewMode) {
        Notice.#withIcon(content, title, iconClasses, Dialog.TypeEnum.error, position, viewMode);
    }

    /**
     * Affiche une notice d'alerte avec icône - titre et continu
     * @param {String|Node}content le continu de la notice
     * @param {String=}title le titre de la notice
     * @param {String} position la position left,right ....
     * @param {String=}iconClasses url de image si viewMode est notice-with-image
     * @param {viewMode=} viewMode le mode d'affichage de la notice  |si null on utilise le mode fill content
     * Note: si null par défaut notice simple
     */
    static warningWithIcon(content, title, iconClasses, position = Notice.PositionEnum.topRight, type, viewMode = Notice.viewMode) {
        Notice.#withIcon(content, title, iconClasses, Dialog.TypeEnum.warning, position, viewMode);
    }

    /**
     * Affiche une notice de succès avec icône - titre et continu
     * @param {String|Node}content le continu de la notice
     * @param {String}title le titre de la notice
     * @param {String} position la position left,right ....
     * @param {String}iconClasses url de image si viewMode est notice-with-image
     * @param {viewMode=} viewMode le mode d'affichage de la notice  |si null on utilise le mode fill content
     */
    static successWithIcon(content, title, iconClasses, position = Notice.PositionEnum.topRight, type, viewMode = Notice.viewMode) {
        Notice.#withIcon(content, title, iconClasses, Dialog.TypeEnum.success, position, viewMode);
    }

    /**
     * affiche une notice d'info
     * @param {String|Node}content le continu de la notice
     * @param {String} position la position left,right ....
     * @param {viewMode=} viewMode le mode d'affichage de la notice  |si null on utilise le mode fill content
     * Note: si null par défaut notice simple
     */
    static info(content, position = Notice.PositionEnum.topRight, viewMode = Notice.viewMode) {
        this.#simple(content, position, Dialog.TypeEnum.info, viewMode);
    }

    /**
     * affiche une notice simple avec texte erreur
     * @param {String|Node}content le continu de la notice
     * @param {String} position la position left,right ...
     * @param {viewMode=} viewMode le mode d'affichage de la notice  |si null on utilise le mode fill content
     * Note: si null par défaut notice simple
     */
    static error(content, position = Notice.PositionEnum.topRight, viewMode = Notice.viewMode) {
        this.#simple(content, position, Dialog.TypeEnum.error, viewMode);
    }

    /**
     * affiche une notice d'alerte simple contenant seulement un texte
     * @param {String|Node}content le continu de la notice
     * @param {String} position la position left,right ....
     * @param {viewMode=} viewMode le mode d'affichage de la notice  |si null on utilise le mode fill content
     * Note: si null par défaut notice simple
     */
    static warning(content, position = Notice.PositionEnum.topRight, viewMode = Notice.viewMode) {
        this.#simple(content, position, Dialog.TypeEnum.warning, viewMode);
    }

    /**
     * affiche une notice succès simple contenant seulement un texte
     * @param {String|Node}content le continu de la notice
     * @param {String} position la position left,right ....
     * @param {viewMode=} viewMode le mode d'affichage de la notice  |si null on utilise le mode fill content
     * Note: si null par défaut notice simple
     */
    static success(content, position = Notice.PositionEnum.topRight, viewMode = Notice.viewMode) {
        this.#simple(content, position, Dialog.TypeEnum.success, viewMode);
    }

    /**
     * Afficher une notice
     * @param {string}content le continu de la notice
     * @param {String=} position la position de la notice
     * @param {Dialog.TypeEnum}type le type de notice alert|info.....
     * @param {viewMode=} viewMode le mode d'affichage de la notice  |si null on utilise le mode fill content
     */
    static #simple(content, position = Notice.PositionEnum.topRight, type, viewMode = Notice.viewMode) {
        Swal.fire({
            toast: true,
            html: content,
            showConfirmButton: false,
            icon: type,
            position: position,
            timer: 4000,
            timerProgressBar: true,
            didOpen: (toast) => {
                toast.addEventListener('mouseenter', Swal.stopTimer)
                toast.addEventListener('mouseleave', Swal.resumeTimer)
            }
        });
    }

    /**
     * @param {String|Node}content le continu de la notice
     * @param {String}title le titre de la notice
     * @param {String} position la position left,right ....
     * @param {Dialog.TypeEnum}  type le type d'alerte
     * @param {String}imageURL url de image
     * @param {viewMode} viewMode le mode d'affichage de la notice
     */
    static #withImage(content, title, imageURL, type, position, viewMode) {
        let notice = this.#_templateNoticeWithImage({
            'type': type,
            'position': position,
            'imageURL': imageURL,
            'title': title,
            'content': content
        });
        $('body').appendTo(notice);
    }

    /**
     * @param {String|Node}content le continu de la notice
     * @param {String}title le titre de la notice
     * @param {String} position la position left,right ....
     * @param {Dialog.TypeEnum}  type le type d'alerte
     * @param {String}iconClasses url de image si viewMode est notice-with-image
     * @param {viewMode} viewMode le mode d'affichage de la notice
     */
    static #withIcon(content, title, iconClasses, type, position, viewMode) {
        let notice = this.#_templateNoticeWithIcon({
            'type': type,
            'position': position,
            'iconClasses': iconClasses,
            'content': content
        });
        $("body").appendTo(notice);
    }

}

//détection des templates pour notice avec image / icon + la template modal bootstrap
$(function () {
    let noticeIconTemplate = $("#notice-icon");
    let noticeImageTemplate = $("#notice-image");
    let bootstrapModalTemplate = $("#template-fenetre-modal")
    if (noticeIconTemplate.length > 0) {
        Notice.templateNoticeWithIcon = _.template(noticeIconTemplate.text());
    }
    if (noticeImageTemplate.length > 0) {
        Notice.templateNoticeWithImage = _.template(noticeImageTemplate.text());
    }
    if (bootstrapModalTemplate.length > 0) {
        Dialog.bootstrapModalTemplate = _.template(bootstrapModalTemplate.text());
    }
});
export {
    Dialog,
    Notice
}
